'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface NavLink {
  href: string;
  label: string;
}

const navLinks: NavLink[] = [
  { href: '/', label: 'Home' },
  { href: '#features', label: 'Features' },
  { href: '#how-it-works', label: 'How it Works' },
  { href: '/contact', label: 'Contact' },
];

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = (): void => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-[0_1px_4px_rgba(27,94,32,0.08)]'
          : 'bg-white'
      }`}
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 flex-shrink-0">
            <AppLogo
              src="/assets/images/MERISTEM-LOGO_ob-1781817309617.png"
              size={36}
            />
            <div className="flex flex-col">
              <span
                className="text-sm font-bold leading-tight tracking-wide"
                style={{ color: '#1B5E20', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                MERISTEM
              </span>
              <span
                className="text-[11px] font-medium leading-tight"
                style={{ color: '#4CAF50', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Digital
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.href.startsWith('#')) {
                if (pathname === '/') {
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.querySelector(link.href);
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-sm font-medium text-gray-600 hover:text-[#1B5E20] transition-colors duration-200"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {link.label}
                    </a>
                  );
                } else {
                  return (
                    <Link
                      key={link.label}
                      href={`/${link.href}`}
                      className="text-sm font-medium text-gray-600 hover:text-[#1B5E20] transition-colors duration-200"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {link.label}
                    </Link>
                  );
                }
              } else {
                return (
                  <Link
                    key={link.label}
                    href={link.href}
                    className="text-sm font-medium text-gray-600 hover:text-[#1B5E20] transition-colors duration-200"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                );
              }
            })}
          </div>

          {/* Login Button - Desktop */}
          <div className="hidden md:flex items-center">
            <Link
              href="/login-screen"
              className="inline-flex items-center px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background: 'linear-gradient(135deg, #1B5E20, #4CAF50)',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              Login
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 border-t border-gray-100 ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 border-t-transparent'
        }`}
      >
        <div
          className="bg-white px-4 py-4 space-y-1"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {navLinks.map((link) => {
            if (link.href.startsWith('#')) {
              if (pathname === '/') {
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMobileMenuOpen(false);
                      const el = document.querySelector(link.href);
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-[#1B5E20]/5 hover:text-[#1B5E20] transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                );
              } else {
                return (
                  <Link
                    key={link.label}
                    href={`/${link.href}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-[#1B5E20]/5 hover:text-[#1B5E20] transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                );
              }
            } else {
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-[#1B5E20]/5 hover:text-[#1B5E20] transition-colors duration-200"
                >
                  {link.label}
                </Link>
              );
            }
          })}
          <div className="pt-2 px-3">
            <Link
              href="/login-screen"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #1B5E20, #4CAF50)',
              }}
            >
              Login
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
